import { Highcharts } from "extensions/highcharts";
import { _ } from "extensions/underscore";

import { accounting } from "common/utils";
import { toHex } from "common/colors";
import strings from "common/strings";
import { getDefaultCurrentCurrency, formatAmountWithCurrentCurrency, amount, number } from "common/formats";
import { v2 } from "common/config/api";

import { DefaultTypeConfig } from "./default";

export const sales = function(options = {}) {
    const opts = _.deepExtend({
        by: "amount",
        chart: {
            yAxis: [],
            series: []
        }
    }, options);
    
    return new DefaultTypeConfig(opts, {
        urlRoot: "analytics/settlements",
        api_endpoint(pivot = "date_time") {
            return v2.settlements.byPivot(Array.isArray(pivot) ? pivot : [pivot]);
        },
        totals: {
            sales_amount: _.partial(accounting.sales.getTotalValue, _, "amount"),
            sales_count: _.partial(accounting.sales.getTotalValue, _, "count")
        },
        quick: {
            editable: true,
            chart: {
                type: "AreaLine",
                series: [{
                    type: "area",
                    pointFormat: [DefaultTypeConfig.date_unix, `sales_${opts.by}`],
                    extend: {
                        fillColor: Highcharts.Color(toHex("canvas-white")).setOpacity(0.15).get("rgba"),
                        color: toHex("canvas-white")
                    }
                }]
            },
            data: {
                summaryTemplate: "summary3",
                widgetValueTemplateData: {
                    totalCount: {
                        value: "sales_count",
                        headerTemplateKey: opts.by === "count" ? "val1" : "val2",
                        activeValue: true,
                        label: strings.totalCount,
                        type: "number"
                    },
                    totalSales: {
                        value: "sales_amount",
                        headerTemplateKey: opts.by !== "count" ? "val1" : "val2",
                        activeValue: true,
                        label: strings.totalAmount,
                        type: "amount"
                    }
                },
                widgetNote: `by ${opts.by}`
            },
            defaultMetric: opts.by,
            reactProps: {
                chartField: "sales",
                chartFieldToggle: true,
                chartFieldDataType: "by",
                secondaryField: "sales",
                secondaryFieldToggle: true,
                secondaryFieldDataType: "by",
                fillColor: Highcharts.Color(toHex("canvas-white")).setOpacity(0.15).get("rgba"),
                color: toHex("canvas-white")
            }
        },
        trendline: {
            summary: {
                enabled: true,
                totals(totals) {
                    return [
                        {
                            name: strings.totalCount,
                            value: totals["sales_count"],
                            valueType: "count",
                            className: "sales-count"
                        },
                        {
                            name: strings.totalAmount,
                            value: totals["sales_amount"],
                            valueType: "amount",
                            className: "sales-amount"
                        }
                    ];
                }
            },
            reactProps: {
                showBadgeInTabs: false,
                countField: "sales_count",
                otherCountField: "sales_amount",
                summaryTitle: "SALES",
                countFieldGetter: "5af0f36edcfa183102e8df3f", // sales_count
                otherCountFieldGetter: "5af0f36edcfa183102e8df3d", // sales_amount
                primaryLineColor: toHex("green-mid")
            }
        },
        table: {
            columns: [
                {
                    id: "Actor",
                    header: "", // This field is changed by tableView.js
                    template(model, pivot) {
                        return DefaultTypeConfig.references.toAlias(model[pivot], pivot);
                    },
                    sort: "string"
                }, {
                    id: "sales_amount",
                    header: strings.salesAmount,
                    template: (model) => {
                        const { currency, decimals } = getDefaultCurrentCurrency();
                        return formatAmountWithCurrentCurrency(model.sales_amount, {
                            abbreviate: true, symbol: currency, symbolPosition: 1, precision: decimals
                        });
                    },
                    css: "amount",
                    initialSorting: ["sales_amount", "desc", "int"],
                    sort: "int"
                }, {
                    id: "sales_count",
                    header: strings.salesCount,
                    format: number,
                    css: "value",
                    sort: "int"
                },
                {
                    id: strings.averageTicket,
                    template: (model) => amount(accounting.sales.averageTicket(model)),
                    header: strings.averageTicket,
                    css: "amount",
                    sort: (a, b) => (accounting.sales.averageTicket(a) < accounting.sales.averageTicket(b) ? -1 : 1)
                }
            ]
        },
        pie: {
            pointValue: `sales_${opts.by}`,
            pointType: opts.by,
            defaultMetric: opts.by
        },
        doublePivot: {
            by: opts.by,
            dataGetter: _.partial(accounting.sales.getTotalValue, _, opts.by),
            bodyType: "table",
            showBadgeInTabNavi: false,
            columns(o) {
                return [{
                    name: o.secondaryPivotString,
                    attribute(model) {
                        return DefaultTypeConfig.references.toAlias(model.get(o.secondaryPivotKey), o.secondaryPivotKey);
                    },
                    cell: "text",
                    sortable: true
                }, {
                    name: strings.count,
                    attribute: _.partial(accounting.sales.getTotalValue, _, "count"),
                    header: {
                        title: strings.salesCount
                    },
                    cell: "number",
                    sortable: true,
                    sortOptions: {
                        active: "-1"
                    }
                }, {
                    name: strings.amount,
                    attribute(model) {
                        if (model.has("original_sales_amount")) {
                            return model.get("original_sales_amount");
                        }
                        
                        return model.get("sales_amount");
                    },
                    cell: "amount",
                    cellOptions: {
                        currency: {
                            attribute: "currency_alias"
                        }
                    },
                    sortable: true
                }];
            },
            summary: {
                enabled: true,
                totals({ collection }) {
                    const totals = collection.totals({
                        count: _.partial(accounting.sales.getTotalValue, _, "count"),
                        amount: _.partial(accounting.sales.getTotalValue, _, "amount")
                    });
                    
                    return [{
                        name: "Count",
                        value: totals.count,
                        valueType: "count"
                    }, {
                        name: "Amount",
                        value: totals.amount,
                        valueType: "amount"
                    }];
                }
            },
            reactProps: {
                showBadgeInTabs: false,
                tablePivot: "currency_alias",
                tableHeaders: ["TRANSACTION CURRENCY", "TRANSACTION COUNT", "TRANSACTION AMOUNT"],
                countField: "sales_count",
                otherCountFields: ["sales_amount"],
                countFieldGetter: "5af0f36edcfa183102e8df3f", // sales_count
                otherCountFieldGetter: "5af0f36edcfa183102e8df3d", // sales_amount
                summaryStyle: 3,
                summaryTitle: "SALES",
                tooltipPrefix: "Sale",
                chart: "table"
            }
        }
    });
};
